import getNestedPropertyValue from "../../common/getNestedPropertyValue";
import { PropertyDisplayFormat } from "../Models/PropertyDisplayFormat";

export type PiiKind = "email" | "phone" | "name";

type MaskDisplayValueOptions = {
  enabled: boolean;
  fieldPath?: string;
  label?: string;
  observedPaths?: string[];
  format?: PropertyDisplayFormat;
};

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const EMAIL_FIELD_SEGMENTS = ["email", "emailaddress"];
const PHONE_FIELD_SEGMENTS = ["phone", "phonenumber", "mobile", "cell", "cellphone"];
const NAME_FIELD_SEGMENTS = ["first", "firstname", "givenname", "last", "lastname", "surname", "familyname", "name"];
const NAME_LABEL_SEGMENTS = ["buyer", "seller", "agent", "customer", "contact", "person", "client", "lender", "escrowagent"];

function getPathSegments(path: string): string[] {
  return path
    .replace(/^\$/, "")
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function normalizeSegment(segment: string): string {
  return segment.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function getPiiKindForFieldPath(path: string | undefined): PiiKind | undefined {
  if (!path) {
    return undefined;
  }

  const segments = getPathSegments(path).map(normalizeSegment);
  const lastSegment = segments[segments.length - 1];

  if (!lastSegment) {
    return undefined;
  }

  if (EMAIL_FIELD_SEGMENTS.some((segment) => lastSegment.includes(segment))) {
    return "email";
  }

  if (PHONE_FIELD_SEGMENTS.some((segment) => lastSegment.includes(segment))) {
    return "phone";
  }

  if (isNameFieldSegment(lastSegment)) {
    return "name";
  }

  return undefined;
}

function getPiiKindForLabel(label: string | undefined): PiiKind | undefined {
  if (!label) {
    return undefined;
  }

  const normalizedLabel = normalizeSegment(label);

  if (EMAIL_FIELD_SEGMENTS.some((segment) => normalizedLabel.includes(segment))) {
    return "email";
  }

  if (PHONE_FIELD_SEGMENTS.some((segment) => normalizedLabel.includes(segment))) {
    return "phone";
  }

  if (NAME_LABEL_SEGMENTS.some((segment) => normalizedLabel.includes(segment)) || isNameFieldSegment(normalizedLabel)) {
    return "name";
  }

  return undefined;
}

function isNameFieldSegment(segment: string): boolean {
  return NAME_FIELD_SEGMENTS.some((nameSegment) => segment === nameSegment || segment.includes(nameSegment)) || segment.endsWith("name");
}

export function isPiiPath(path: string | undefined): boolean {
  return !!getPiiKindForFieldPath(path);
}

function maskMiddle(value: string, visibleStart = 1, visibleEnd = 1): string {
  if (value.length <= visibleStart + visibleEnd) {
    return value.length <= 1 ? "*" : `${value[0]}*`;
  }

  return `${value.slice(0, visibleStart)}${"*".repeat(value.length - visibleStart - visibleEnd)}${value.slice(value.length - visibleEnd)}`;
}

function maskEmail(value: string): string {
  return value.replace(EMAIL_PATTERN, (email) => {
    const [localPart, domain] = email.split("@");
    if (!localPart || !domain) {
      return maskMiddle(email);
    }

    return `${maskMiddle(localPart, 1, localPart.length > 4 ? 1 : 0)}@${domain}`;
  });
}

function maskPhone(value: string): string {
  const digitCount = (value.match(/\d/g) ?? []).length;

  if (digitCount <= 4) {
    return value.replace(/\d/g, "*");
  }

  let seenDigits = 0;
  return value.replace(/\d/g, (digit) => {
    seenDigits += 1;
    return seenDigits <= 2 || seenDigits > digitCount - 2 ? digit : "*";
  });
}

function maskName(value: string): string {
  return value.replace(/[A-Za-z]+/g, (word) => maskMiddle(word, 1, word.length > 3 ? 1 : 0));
}

function collectPiiKinds(options: MaskDisplayValueOptions): Set<PiiKind> {
  const kinds = new Set<PiiKind>();

  if (options.format === PropertyDisplayFormat.EmailLink) {
    kinds.add("email");
  }

  if (options.format === PropertyDisplayFormat.PhoneLink) {
    kinds.add("phone");
  }

  const fieldKind = getPiiKindForFieldPath(options.fieldPath);
  if (fieldKind) {
    kinds.add(fieldKind);
  }

  const labelKind = getPiiKindForLabel(options.label);
  if (labelKind) {
    kinds.add(labelKind);
  }

  for (const path of options.observedPaths ?? []) {
    const pathKind = getPiiKindForFieldPath(path);
    if (pathKind) {
      kinds.add(pathKind);
    }
  }

  return kinds;
}

export function maskDisplayValue(value: unknown, options: MaskDisplayValueOptions): string {
  const text = value === null || value === undefined ? "" : value.toString();

  if (!options.enabled || !text) {
    return text;
  }

  const kinds = collectPiiKinds(options);
  if (kinds.size === 0) {
    return text;
  }

  let masked = text;
  if (kinds.has("email")) {
    masked = maskEmail(masked);
  }

  if (kinds.has("phone")) {
    masked = maskPhone(masked);
  }

  if (kinds.has("name")) {
    masked = maskName(masked);
  }

  return masked;
}

export function formatTemplateForDisplay(
  template: string,
  values: Record<string, any>,
  options: Omit<MaskDisplayValueOptions, "observedPaths">,
  observedPaths?: string[]
): string {
  if (!template.includes("{") || !template.includes("}")) {
    return template;
  }

  return template.replace(/\{([^}]+)\}/g, (match, key) => {
    observedPaths?.push(key);

    const value = getNestedPropertyValue(key, values);
    if (value === null || value === undefined) {
      return "";
    }

    return maskDisplayValue(value, {
      ...options,
      fieldPath: key,
      label: undefined,
      observedPaths: [key],
    });
  });
}
